import React, { useState, useEffect } from 'react';
import { masterDataAPI } from '../../api';
import { Loading } from '../../components';
import './MasterData.css';

const MasterData = () => {
  const [activeTab, setActiveTab] = useState('sampleNames');
  const [sampleNames, setSampleNames] = useState([]);
  const [waterSources, setWaterSources] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({ name: '' });

  // District modal state
  const [showDistrictModal, setShowDistrictModal] = useState(false);
  const [districtModalMode, setDistrictModalMode] = useState('add');
  const [selectedState, setSelectedState] = useState(null);
  const [editDistrict, setEditDistrict] = useState(null);
  const [districtFormData, setDistrictFormData] = useState({ name: '' });

  // View districts modal state
  const [showViewDistrictsModal, setShowViewDistrictsModal] = useState(false);
  const [viewingState, setViewingState] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const tabs = [
    { id: 'sampleNames', label: 'Sample Names' },
    { id: 'waterSources', label: 'Sample Sources' },
    { id: 'locationTypes', label: 'Location Types' },
    { id: 'stateDistrict', label: 'State/District' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [snResponse, wsResponse, ltResponse, stResponse] = await Promise.all([
        masterDataAPI.getSampleNames(true).catch(() => ({ data: { data: [] } })),
        masterDataAPI.getWaterSources(true),
        masterDataAPI.getLocationTypes(true),
        masterDataAPI.getStates(true)
      ]);

      setSampleNames(snResponse.data?.data || []);
      setWaterSources(wsResponse.data?.data || []);
      setLocationTypes(ltResponse.data?.data || []);
      setStates(stResponse.data?.data || []);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setEditItem(null);
    setFormData({ name: '' });
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setModalMode('edit');
    setEditItem(item);
    setFormData({ name: item.name });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditItem(null);
    setFormData({ name: '' });
  };

  // District modal functions
  const openAddDistrictModal = (state) => {
    setDistrictModalMode('add');
    setSelectedState(state);
    setEditDistrict(null);
    setDistrictFormData({ name: '' });
    setShowDistrictModal(true);
  };

  const openEditDistrictModal = (state, district) => {
    setDistrictModalMode('edit');
    setSelectedState(state);
    setEditDistrict(district);
    setDistrictFormData({ name: district.name });
    setShowDistrictModal(true);
  };

  const closeDistrictModal = () => {
    setShowDistrictModal(false);
    setSelectedState(null);
    setEditDistrict(null);
    setDistrictFormData({ name: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setSaving(true);

      if (activeTab === 'sampleNames') {
        if (modalMode === 'add') {
          await masterDataAPI.createSampleName(formData);
          setSuccess('Sample name added successfully');
        } else {
          await masterDataAPI.updateSampleName(editItem._id, formData);
          setSuccess('Sample name updated successfully');
        }
      } else if (activeTab === 'waterSources') {
        if (modalMode === 'add') {
          await masterDataAPI.createWaterSource(formData);
          setSuccess('Sample source added successfully');
        } else {
          await masterDataAPI.updateWaterSource(editItem._id, formData);
          setSuccess('Sample source updated successfully');
        }
      } else if (activeTab === 'locationTypes') {
        if (modalMode === 'add') {
          await masterDataAPI.createLocationType(formData);
          setSuccess('Location type added successfully');
        } else {
          await masterDataAPI.updateLocationType(editItem._id, formData);
          setSuccess('Location type updated successfully');
        }
      } else if (activeTab === 'stateDistrict') {
        if (modalMode === 'add') {
          await masterDataAPI.createState(formData);
          setSuccess('State added successfully');
        } else {
          await masterDataAPI.updateState(editItem._id, formData);
          setSuccess('State updated successfully');
        }
      }

      setTimeout(() => setSuccess(null), 3000);
      closeModal();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDistrictSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!districtFormData.name.trim()) {
      setError('District name is required');
      return;
    }

    try {
      setSaving(true);

      if (districtModalMode === 'add') {
        await masterDataAPI.addDistrict(selectedState._id, districtFormData);
        setSuccess('District added successfully');
      } else {
        await masterDataAPI.updateDistrict(selectedState._id, editDistrict._id, districtFormData);
        setSuccess('District updated successfully');
      }

      setTimeout(() => setSuccess(null), 3000);
      closeDistrictModal();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save district');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      if (activeTab === 'sampleNames') {
        await masterDataAPI.updateSampleName(item._id, { isActive: !item.isActive });
      } else if (activeTab === 'waterSources') {
        await masterDataAPI.updateWaterSource(item._id, { isActive: !item.isActive });
      } else if (activeTab === 'locationTypes') {
        await masterDataAPI.updateLocationType(item._id, { isActive: !item.isActive });
      } else if (activeTab === 'stateDistrict') {
        await masterDataAPI.updateState(item._id, { isActive: !item.isActive });
      }
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const handleToggleDistrictActive = async (state, district) => {
    try {
      await masterDataAPI.updateDistrict(state._id, district._id, { isActive: !district.isActive });
      const response = await masterDataAPI.getStates(true);
      const updatedStates = response.data?.data || [];
      setStates(updatedStates);
      // Update viewing state if modal is open
      if (viewingState && viewingState._id === state._id) {
        const updatedState = updatedStates.find(s => s._id === state._id);
        if (updatedState) setViewingState(updatedState);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update district status');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete "${item.name}"?`)) return;

    try {
      if (activeTab === 'sampleNames') {
        await masterDataAPI.deleteSampleName(item._id);
        setSuccess('Sample name deleted successfully');
      } else if (activeTab === 'waterSources') {
        await masterDataAPI.deleteWaterSource(item._id);
        setSuccess('Sample source deleted successfully');
      } else if (activeTab === 'locationTypes') {
        await masterDataAPI.deleteLocationType(item._id);
        setSuccess('Location type deleted successfully');
      } else if (activeTab === 'stateDistrict') {
        await masterDataAPI.deleteState(item._id);
        setSuccess('State deleted successfully');
      }
      setTimeout(() => setSuccess(null), 3000);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete');
    }
  };

  const handleDeleteDistrict = async (state, district) => {
    if (!window.confirm(`Are you sure you want to delete district "${district.name}"?`)) return;

    try {
      await masterDataAPI.deleteDistrict(state._id, district._id);
      setSuccess('District deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
      const response = await masterDataAPI.getStates(true);
      const updatedStates = response.data?.data || [];
      setStates(updatedStates);
      // Update viewing state if modal is open
      if (viewingState && viewingState._id === state._id) {
        const updatedState = updatedStates.find(s => s._id === state._id);
        if (updatedState) setViewingState(updatedState);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete district');
    }
  };

  const openViewDistrictsModal = (state) => {
    setViewingState(state);
    setShowViewDistrictsModal(true);
  };

  const closeViewDistrictsModal = () => {
    setShowViewDistrictsModal(false);
    setViewingState(null);
  };

  if (loading) return <Loading message="Loading master data..." />;

  const getCurrentData = () => {
    if (activeTab === 'sampleNames') return sampleNames;
    if (activeTab === 'waterSources') return waterSources;
    if (activeTab === 'locationTypes') return locationTypes;
    if (activeTab === 'stateDistrict') return states;
    return [];
  };

  const getItemLabel = () => {
    if (activeTab === 'sampleNames') return 'Sample Name';
    if (activeTab === 'waterSources') return 'Sample Source';
    if (activeTab === 'locationTypes') return 'Location Type';
    if (activeTab === 'stateDistrict') return 'State';
    return '';
  };

  const getTabCount = (tabId) => {
    if (tabId === 'sampleNames') return sampleNames.length;
    if (tabId === 'waterSources') return waterSources.length;
    if (tabId === 'locationTypes') return locationTypes.length;
    if (tabId === 'stateDistrict') return states.length;
    return 0;
  };

  const currentData = getCurrentData();
  const itemLabel = getItemLabel();

  // Pagination calculations
  const totalItems = currentData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = currentData.slice(startIndex, endIndex);

  return (
    <div className="master-data-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Source</h1>
          <p>Manage sample names, sample sources, location types, and state/district for sample collection</p>
        </div>
        <div className="header-actions">
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="master-data-container">
        {/* Tab Navigation */}
        <div className="tabs-nav">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.id); setCurrentPage(1); }}
            >
              <span className="tab-label">{tab.label}</span>
              <span className="tab-count">{getTabCount(tab.id)}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="data-card">
          <div className="card-header">
            <h2>
              {activeTab === 'sampleNames' && 'Sample Names'}
              {activeTab === 'waterSources' && 'Sample Sources'}
              {activeTab === 'locationTypes' && 'Location Types'}
              {activeTab === 'stateDistrict' && 'States & Districts'}
            </h2>
            <button className="btn btn-primary" onClick={openAddModal}>
              + Add {itemLabel}
            </button>
          </div>

          <div className="table-container">
            <table className={`table ${activeTab === 'stateDistrict' ? 'state-district-table' : ''}`}>
              <thead>
                <tr>
                  <th className="serial-cell">S.No.</th>
                  <th className="name-cell">Name</th>
                  {activeTab === 'stateDistrict' && <th className="districts-cell">Districts</th>}
                  <th className="status-cell">Status</th>
                  <th className="actions-cell">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentData.length === 0 ? (
                  <tr>
                    <td colSpan={activeTab === 'stateDistrict' ? 5 : 4} className="empty-state">
                      No {itemLabel.toLowerCase()}s found. Click "Add {itemLabel}" to get started.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, index) => (
                    <React.Fragment key={item._id}>
                      <tr className={!item.isActive ? 'inactive-row' : ''}>
                        <td className="serial-cell">{startIndex + index + 1}</td>
                        <td className="name-cell">{item.name}</td>
                        {activeTab === 'stateDistrict' && (
                          <td className="districts-cell">
                            <button
                              className="district-toggle-btn"
                              onClick={() => openViewDistrictsModal(item)}
                            >
                              <span className="district-count">{item.districts?.length || 0}</span>
                              <span className="district-label">
                                {item.districts?.length === 1 ? 'District' : 'Districts'}
                              </span>
                            </button>
                          </td>
                        )}
                        <td className="status-cell">
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={item.isActive}
                              onChange={() => handleToggleActive(item)}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => openEditModal(item)}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(item)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <div className="pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} of {totalItems} entries
              </div>
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </button>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="pagination-pages">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
                <button
                  className="pagination-btn"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal for Water Source, Location Type, State */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {modalMode === 'add' ? `Add ${itemLabel}` : `Edit ${itemLabel}`}
              </h3>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={`Enter ${itemLabel.toLowerCase()} name`}
                    required
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : modalMode === 'add' ? `Add ${itemLabel}` : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit District Modal */}
      {showDistrictModal && (
        <div className="modal-overlay" onClick={closeDistrictModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {districtModalMode === 'add' ? 'Add District' : 'Edit District'}
                {selectedState && <span className="modal-subtitle"> - {selectedState.name}</span>}
              </h3>
              <button className="modal-close" onClick={closeDistrictModal}>&times;</button>
            </div>

            <form onSubmit={handleDistrictSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">District Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={districtFormData.name}
                    onChange={(e) => setDistrictFormData({ ...districtFormData, name: e.target.value })}
                    placeholder="Enter district name"
                    required
                  />
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={closeDistrictModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : districtModalMode === 'add' ? 'Add District' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Districts Modal */}
      {showViewDistrictsModal && viewingState && (
        <div className="modal-overlay" onClick={closeViewDistrictsModal}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                Districts in {viewingState.name}
                <span className="modal-count">{viewingState.districts?.length || 0}</span>
              </h3>
              <button className="modal-close" onClick={closeViewDistrictsModal}>&times;</button>
            </div>

            <div className="modal-body modal-body-table">
              <div className="modal-table-header">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => { closeViewDistrictsModal(); openAddDistrictModal(viewingState); }}
                >
                  + Add District
                </button>
              </div>

              {viewingState.districts && viewingState.districts.length > 0 ? (
                <table className="table districts-modal-table">
                  <thead>
                    <tr>
                      <th className="serial-cell">S.No.</th>
                      <th className="name-cell">District Name</th>
                      <th className="status-cell">Status</th>
                      <th className="actions-cell">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {viewingState.districts.map((district, dIndex) => (
                      <tr key={district._id} className={!district.isActive ? 'inactive-row' : ''}>
                        <td className="serial-cell">{dIndex + 1}</td>
                        <td className="name-cell">{district.name}</td>
                        <td className="status-cell">
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={district.isActive}
                              onChange={() => handleToggleDistrictActive(viewingState, district)}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </td>
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => { closeViewDistrictsModal(); openEditDistrictModal(viewingState, district); }}
                            >
                              Edit
                            </button>
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteDistrict(viewingState, district)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-districts">
                  No districts added yet. Click "+ Add District" to add one.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MasterData;
